import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  IconButton,
  ListItemIcon,
  FormControlLabel,
  Checkbox,
  Chip
} from '@mui/material';
import {
  CardGiftcard as GiftIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MonetizationOn as CoinsIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { giftApi } from '../api/apiService';
import { Gift } from '../types/models';

const ManageGiftsPage = () => {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [giftToDelete, setGiftToDelete] = useState<Gift | null>(null);
  const [editingGift, setEditingGift] = useState<Gift | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    coinsRequired: '',
    description: '',
    url: '',
    quantity: '',
    unlimited: false
  });

  const fetchGifts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await giftApi.getAll();
      setGifts(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load gifts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts]);

  const handleOpenDialog = (gift?: Gift) => {
    if (gift) {
      setEditingGift(gift);
      const isUnlimited = gift.quantity === null || gift.quantity === undefined;
      setFormData({
        name: gift.name,
        coinsRequired: gift.coinsRequired.toString(),
        description: gift.description || '',
        url: gift.url || '',
        quantity: isUnlimited ? '' : gift.quantity!.toString(),
        unlimited: isUnlimited
      });
    } else {
      setEditingGift(null);
      setFormData({ name: '', coinsRequired: '', description: '', url: '', quantity: '', unlimited: false });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGift(null);
    setFormData({ name: '', coinsRequired: '', description: '', url: '', quantity: '', unlimited: false });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.coinsRequired) return;

    try {
      setSubmitting(true);
      setError(null);

      const giftData: any = {
        name: formData.name.trim(),
        coinsRequired: parseInt(formData.coinsRequired, 10),
        description: formData.description.trim(),
        url: formData.url.trim(),
        unlimited: formData.unlimited
      };

      if (!formData.unlimited && formData.quantity) {
        giftData.quantity = parseInt(formData.quantity, 10);
      }

      if (editingGift?.id) {
        const response = await giftApi.update(editingGift.id, giftData);
        setGifts(gifts.map(g => g.id === editingGift.id ? response.data : g));
        setSuccess('Gift updated successfully!');
      } else {
        const response = await giftApi.create(giftData);
        setGifts([...gifts, response.data]);
        setSuccess('Gift created successfully!');
      }

      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save gift');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (gift: Gift) => {
    setGiftToDelete(gift);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!giftToDelete?.id) return;

    try {
      setDeleting(true);
      setError(null);
      await giftApi.delete(giftToDelete.id);
      setGifts(gifts.filter(g => g.id !== giftToDelete.id));
      setSuccess('Gift deleted successfully!');
      setDeleteDialogOpen(false);
      setGiftToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete gift');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGiftToDelete(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex'
            }}>
              <GiftIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                Manage Gifts
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {gifts.length} gift{gifts.length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Add Gift
          </Button>
        </Box>
        <CardContent>
          {gifts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GiftIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No gifts added yet. Click "Add Gift" to get started!
              </Typography>
            </Box>
          ) : (
            <List>
              {gifts.map((gift, index) => (
                <ListItem
                  key={gift.id}
                  divider={index < gifts.length - 1}
                  sx={{ px: 0 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleOpenDialog(gift)}
                        sx={{ color: 'primary.main' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteClick(gift)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: 'secondary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <GiftIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {gift.name}
                        </Typography>
                        {gift.url && (
                          <LinkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <CoinsIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                          <Typography variant="body2" color="text.secondary">
                            {gift.coinsRequired} coins
                          </Typography>
                          <Typography variant="body2" color="text.disabled" sx={{ mx: 1 }}>
                            ·
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {gift.quantity === null || gift.quantity === undefined
                              ? 'Unlimited'
                              : `Qty: ${gift.quantity}`}
                          </Typography>
                        </Box>
                        {gift.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {gift.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Gift Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGift ? 'Edit Gift' : 'Add New Gift'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Gift Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Required Coins"
            type="number"
            value={formData.coinsRequired}
            onChange={(e) => setFormData({ ...formData, coinsRequired: e.target.value })}
            margin="normal"
            required
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="URL (Optional)"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            margin="normal"
            placeholder="https://example.com/gift"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.unlimited}
                  onChange={(e) => setFormData({ ...formData, unlimited: e.target.checked, quantity: e.target.checked ? '' : formData.quantity })}
                />
              }
              label="Unlimited quantity"
            />
          </Box>
          {!formData.unlimited && (
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              margin="normal"
              inputProps={{ min: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formData.name.trim() || !formData.coinsRequired}
          >
            {submitting ? <CircularProgress size={24} /> : editingGift ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Gift</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{giftToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageGiftsPage;